import React, { useState, useEffect, useRef, useCallback } from 'react'
import Modal from 'react-responsive-modal'
import styles from './styles.module.css'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import { variableStuffModalModes } from '../../assets/const'
import cn from 'classnames'
import { Main, Admission, Education, ProfessionalSelection, Anthropometry, IOT, EducUniversalActions, SpecialistRecords, FinalGrades, Ege, Family, Benefits, TrustedPersons, AwardAndPunishment, Events, PublicLoad, Documents, Portfolio, IssuedDocuments, PhysicalTraining, Characteristic, Achieve, Hobbies, Health } from './ModalTabs'
import PasswordModal from './PasswordModal'

import { StudentsContext } from '../../utils/context'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import {
  getStudentTable,
  createStudentInfo,
  updateStudentInfo,
  addStudentDeletion,
  clearStudentDeletions,
  TAB_MAPPING
} from '../../actions/students/other'
import { getCountries, getSchoolYears, getSubjects } from '../../actions/common'
import {
  getStudentPhoto,
  uploadStudentPhoto,
  deleteStudentPhoto,
} from '../../actions/students/photo'
import { createNotification } from '../../components/Notifications/Notifications'

const PHOTO_MAX_SIZE = 5 * 1024 * 1024
const PHOTO_ALLOWED_TYPES = ['image/jpeg', 'image/png']


export default function ModalComponent({ studentId, isOpen, closeModal, addMode }) {

  const dispatch = useDispatch()

  const [mode, setMode] = useState('main')
  const [showValidationWarning, setShowValidationWarning] = useState(false)
  const [validationMessage, setValidationMessage] = useState('')
  const [passwordModal, setPasswordModal] = useState({ open: false, type: null })
  const [confirmClose, setConfirmClose] = useState(false)
  // Загрузка по вкладкам — независимо от других вкладок, которые могут грузиться параллельно
  const [loadingTabs, setLoadingTabs] = useState({})

  // Получаем данные из Redux
  const { studentTabData, deletionsTracker } = useSelector(state => state.students.data)

  const photoInputRef = useRef(null)
  const [photoUrl, setPhotoUrl] = useState(null)
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false)

  const [photoLoading, setPhotoLoading] = useState(false)

  // Отслеживание загруженных табов (чтобы не перезагружать при повторном переходе)
  const loadedTabsRef = useRef(new Set())

  // Текущая вкладка в ref — чтобы читать актуальное значение без лишних перезапусков эффекта смены студента
  const modeRef = useRef(mode)
  useEffect(() => {
    modeRef.current = mode
  }, [mode])

  // Диспатчит getStudentTable для конкретной вкладки и отслеживает её собственную загрузку
  // в loadingTabs — независимо от других вкладок, грузящихся параллельно (например main +
  // текущая вкладка при смене студента), чтобы завершение одной не маскировало другую
  const fetchTabData = useCallback((tab, sid) => {
    setLoadingTabs(prev => ({ ...prev, [tab]: true }))
    return dispatch(getStudentTable(tab, sid)).finally(() => {
      setLoadingTabs(prev => ({ ...prev, [tab]: false }))
    })
  }, [dispatch])

  // Загрузка данных при смене таба
  const handleTabChange = (newMode) => {
    setMode(newMode)
    // Загружаем данные таба только в режиме редактирования и если таб ещё не загружен
    if (!addMode && studentId && !loadedTabsRef.current.has(newMode)) {
      loadedTabsRef.current.add(newMode)
      fetchTabData(newMode, studentId)
    }
  }

  // Загрузка данных main-таба и текущей открытой вкладки при открытии модалки / смене студента
  useEffect(() => {
    if (isOpen) {
      // Сбрасываем список загруженных табов — данные относятся к предыдущему студенту
      loadedTabsRef.current = new Set()
      setLoadingTabs({})
    }
    if (!addMode && studentId && isOpen) {
      loadedTabsRef.current.add('main')
      fetchTabData('main', studentId)

      const currentMode = modeRef.current
      // Если открыта не main-вкладка — перезагружаем и её данные для нового студента,
      // иначе на ней остаются результаты запроса по предыдущему studentId
      if (currentMode !== 'main') {
        loadedTabsRef.current.add(currentMode)
        fetchTabData(currentMode, studentId)
      }
    }
  }, [isOpen, studentId, addMode, dispatch, fetchTabData])

  // Сброс на вкладку main при каждом открытии модалки
  useEffect(() => {
    if (isOpen) {
      setMode('main')
    }
  }, [isOpen])

  // Очистка состояния формы при закрытии модалки — чтобы при открытии
  // записи другого студента поля не показывали "устаревшие" данные
  // предыдущего пока не придёт свежий ответ сервера
  useEffect(() => {
    if (!isOpen) {
      allForms.resetForm({ values: getInitialValues() })
      initialValuesRef.current = {}
      loadedTabsRef.current = new Set()
      setLoadingTabs({})
    }
  }, [isOpen])

  // Очистка формы при открытии в режиме добавления
  useEffect(() => {
    if (isOpen && addMode) {
      allForms.resetForm({ values: getInitialValues() })
      initialValuesRef.current = {}
    }
  }, [isOpen, addMode])

  // Загрузка фото учащегося (GET /students/{mid}/photo)
  const loadPhoto = useCallback(async () => {
    if (!studentId) {
      setPhotoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null })
      setPhotoLoading(false)
      return
    }
    setPhotoLoading(true)
    const blob = await dispatch(getStudentPhoto(studentId))
    setPhotoUrl(prev => {
      if (prev) URL.revokeObjectURL(prev)
      return blob ? URL.createObjectURL(blob) : null
    })
    setPhotoLoading(false)
  }, [studentId, dispatch])

  useEffect(() => {
    if (isOpen && !addMode && studentId) {
      loadPhoto()
    } else {
      setPhotoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null })
      setPhotoLoading(false)
    }
  }, [isOpen, studentId, addMode]) // eslint-disable-line

  const handlePhotoSelect = async (file) => {
    if (!file || !studentId) return
    if (!PHOTO_ALLOWED_TYPES.includes(file.type)) {
      createNotification('error', 'Поддерживаются только файлы JPG и PNG')
      return
    }
    if (file.size > PHOTO_MAX_SIZE) {
      createNotification('error', 'Максимальный размер файла — 5 МБ')
      return
    }
    const success = await dispatch(uploadStudentPhoto(studentId, file))
    if (success) loadPhoto()
  }

  const handlePhotoInputChange = (e) => {
    const file = e.target.files[0]
    e.target.value = ''
    handlePhotoSelect(file)
  }

  const handlePhotoDrop = (e) => {
    e.preventDefault()
    setIsDraggingPhoto(false)
    handlePhotoSelect(e.dataTransfer.files[0])
  }

  const handlePhotoDeleteClick = async () => {
    const success = await dispatch(deleteStudentPhoto(studentId))
    if (success) loadPhoto()
  }

  // Загрузка справочников
  useEffect(() => {
    if (isOpen) {
      dispatch(getSchoolYears())
      dispatch(getCountries())
      dispatch(getSubjects())
    }
  }, [isOpen, dispatch])

  // Создание студента
  const handleCreate = async (values) => {
    const success = await dispatch(createStudentInfo(values))
    if (success) {
      closeModal()
    }
  }

  // Обновление студента - собираем только изменённые данные по каждой вкладке
  const handleUpdate = async (values) => {
    const initialValues = initialValuesRef.current
    let payload = {}

    // Хелпер для очистки временных id в массивах
    const cleanArrayData = (arr) => {
      if (!Array.isArray(arr) || !arr.length) return []
      return arr.map(item => {
        if (String(item.id || item.xp_key).startsWith('new_')) {
          const { id, ...rest } = item
          return rest
        }
        return item
      })
    }

    // Словарные вкладки - собираем только изменённые поля
    const dictTabs = ['main', 'admission', 'training', 'profotbor', 'iot', 'anthropometry', 'characteristic', 'health', 'documents', 'family']
    dictTabs.forEach(tab => {
      const dirtyFields = getDirtyFields(values[tab], initialValues[tab])
      if (dirtyFields) {
        payload[tab] = dirtyFields
      }
    })

    // Массивные вкладки - отправляем если были изменения
    const arrayTabs = [
      'privileges',
      'specialist_records',
      'grades',
      'exams',
      'trusted_persons',
      'penalties',
      'events',
      'public_load',
      'severity',
      'portfolio',
      'issued_documents',
      'physical_training',
      'achievements',
      'hobby'
    ]

    arrayTabs.forEach(tab => {
      const currentData = values[tab]
      const initialData = initialValues[tab]
      // Сравниваем массивы
      if (JSON.stringify(currentData) !== JSON.stringify(initialData)) {
        if (Array.isArray(currentData) && currentData.length) {
          payload[tab] = cleanArrayData(currentData)
        }
      }
    })

    // Добавляем поля удаления из трекера
    Object.keys(deletionsTracker || {}).forEach(apiTab => {
      if (deletionsTracker[apiTab] && deletionsTracker[apiTab].length > 0) {
        payload[`del_${apiTab}`] = deletionsTracker[apiTab]
      }
    })

    // Отправляем только если есть изменения
    if (Object.keys(payload).length === 0) {
      return
    }

    const success = await dispatch(updateStudentInfo(studentId, payload))
    if (success) {
      dispatch(clearStudentDeletions())
      // Обновляем начальные значения после успешного сохранения
      // чтобы dirty tracking работал корректно для следующих изменений
      Object.keys(payload).forEach(tab => {
        if (!tab.startsWith('del_')) {
          initialValuesRef.current[tab] = JSON.parse(JSON.stringify(values[tab]))
        }
      })
      // Перезагружаем данные текущей вкладки
      if (studentId) {
        dispatch(getStudentTable(mode, studentId))
      }
    }
  }

  // Трекинг удаления записи
  const trackDeletion = (apiTab, itemId) => {
    dispatch(addStudentDeletion(apiTab, itemId))
  }

  // Начальные значения для Formik - структурированы по вкладкам согласно API
  const getInitialValues = () => {
    return {
      // Словарные вкладки (объекты)
      main: {},
      admission: {},
      training: {},
      profotbor: {},
      iot: {},
      anthropometry: {},
      characteristic: {},
      health: {},
      documents: {},
      family: {},
      privileges: {},
      // Массивные вкладки
      specialist_records: [],
      grades: [],
      exams: [],
      trusted_persons: [],
      penalties: [],
      events: [],
      public_load: [],
      severity: [],
      portfolio: [],
      issued_documents: [],
      physical_training: [],
      achievements: [],
      hobby: []
    }
  }

  // Хелпер для получения только изменённых полей объекта
  const getDirtyFields = (current, initial) => {
    if (!current || typeof current !== 'object') return current
    if (Array.isArray(current)) return current

    const dirty = {}
    Object.keys(current).forEach(key => {
      const currentVal = current[key]
      const initialVal = initial?.[key]
      // Сравниваем значения (приводим к строке для корректного сравнения)
      if (JSON.stringify(currentVal) !== JSON.stringify(initialVal)) {
        dirty[key] = currentVal
      }
    })
    return Object.keys(dirty).length > 0 ? dirty : null
  }

  // Ref для хранения начальных значений (для сравнения при сохранении)
  const initialValuesRef = useRef({})

  // Функция для обновления начальных значений (вызывается из вкладок при загрузке данных)
  const updateInitialValues = useCallback((tab, data) => {
    initialValuesRef.current = {
      ...initialValuesRef.current,
      [tab]: JSON.parse(JSON.stringify(data)) // глубокое копирование
    }
  }, [])

  const allForms = useFormik({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    onSubmit: addMode ? handleCreate : handleUpdate
  })

  const handleClose = useCallback(() => { if (allForms.dirty) setConfirmClose(true); else closeModal() }, [allForms.dirty, closeModal])
  const acceptConfirmation = useCallback(() => { closeModal(); setConfirmClose(false) }, [closeModal])
  const rejectConfirmation = useCallback(() => setConfirmClose(false), [])

  // Проверка заполненности обязательных полей
  const areRequiredFieldsFilled = () => {
    const { values } = allForms
    const mainValues = values.main || {}
    return mainValues.lastname?.trim() && mainValues.firstname?.trim() && mainValues.birthday
  }

  // Валидация и сохранение
  const handleSaveClick = () => {
    const { values } = allForms
    const mainValues = values.main || {}

    if (addMode) {
      if (!mainValues.lastname?.trim()) {
        setValidationMessage('Заполните поле Фамилия во вкладке Главная')
        setShowValidationWarning(true)
        return
      }
      if (!mainValues.firstname?.trim()) {
        setValidationMessage('Заполните поле Имя во вкладке Главная')
        setShowValidationWarning(true)
        return
      }
      if (!mainValues.birthday) {
        setValidationMessage('Заполните поле Дата рождения во вкладке Главная')
        setShowValidationWarning(true)
        return
      }
    }

    allForms.handleSubmit()
  }

  const renderLi = ({ name, value, icon }) => {
    const selected = value === mode
    const isDisabled = addMode && !areRequiredFieldsFilled() && value !== 'main'

    return (
      <li
        key={value}
        onClick={() => !isDisabled && handleTabChange(value)}
        className={cn(styles.modalMenuItem, {
          [styles.selectedMenuItem]: selected,
          [styles.disabledMenuItem]: isDisabled
        })}
      >
        {icon()}
        {name}
      </li>
    )
  }

  const renderTab = () => {
    switch (mode) {
      case 'main': return <Main />
      case 'admission': return <Admission />
      case 'training': return <Education />
      case 'profotbor': return <ProfessionalSelection />
      case 'iot': return <IOT />
      case 'anthropometry': return <Anthropometry />
      case 'severity': return <EducUniversalActions />
      case 'specialist_records': return <SpecialistRecords />
      case 'grades': return <FinalGrades />
      case 'exams': return <Ege />
      case 'family': return <Family />
      case 'privileges': return <Benefits />
      case 'trusted_persons': return <TrustedPersons />
      case 'penalties': return <AwardAndPunishment />
      case 'events': return <Events />
      case 'public_load': return <PublicLoad />
      case 'documents': return <Documents />
      case 'portfolio': return <Portfolio />
      case 'issued_documents': return <IssuedDocuments />
      case 'physical_training': return <PhysicalTraining />
      case 'characteristic': return <Characteristic />
      case 'achievements': return <Achieve />
      case 'hobby': return <Hobbies />
      case 'health': return <Health />
      default: return null
    }
  }

  return (
    <StudentsContext.Provider value={{
      closeModal,
      allForms,
      addMode,
      studentTabData,
      trackDeletion,
      deletionsTracker,
      currentTab: mode,
      studentId,
      updateInitialValues,
      currentTabLoading: !!loadingTabs[mode]
    }}>
      {showValidationWarning && (
        <Confirmation
          title={validationMessage}
          acceptConfirmation={() => setShowValidationWarning(false)}
          rejectConfirmation={() => setShowValidationWarning(false)}
        />
      )}
      <Modal
        open={isOpen}
        onClose={handleClose}
        classNames={{ modal: styles.modalMain }}
        closeOnOverlayClick={true}
        closeOnEsc={true}
        showCloseIcon={false}
        animationDuration={1}
      >
        <div className={styles.modalContent}>
          <div className={styles.modalMenu}>
            <div className={styles.photoSidebar}>
              <input
                ref={photoInputRef}
                type='file'
                accept='image/jpeg,image/png'
                hidden
                onChange={handlePhotoInputChange}
              />
              {photoLoading ? (
                <div className={styles.photoSidebarBox} />
              ) : photoUrl ? (
              <>
              <div
                className={cn(styles.photoSidebarBox, { [styles.photoSidebarBoxActive]: isDraggingPhoto })}
                onClick={() => studentId && photoInputRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDraggingPhoto(true) }}
                onDragLeave={(e) => { e.preventDefault(); setIsDraggingPhoto(false) }}
                onDrop={handlePhotoDrop}
              >
                <img src={photoUrl} alt='' className={styles.photoSidebarImage} />
                <div className={styles.photoSidebarOverlay}>
                      <button type='button' className={styles.photoSidebarBtn} onClick={(e) => { e.stopPropagation(); photoInputRef.current.click() }}>Заменить</button>
                      <button type='button' className={cn(styles.photoSidebarBtn, styles.photoSidebarBtnDanger)} onClick={(e) => { e.stopPropagation(); handlePhotoDeleteClick() }}>Удалить</button>
                </div>
              </div>
              <div className={styles.photoSidebarCaption}>Наведите на фото — заменить или удалить</div>
              </>
              ) : (
              <>
              <div
                className={cn(styles.photoDropzone, { [styles.photoDropzoneActive]: isDraggingPhoto })}
                onClick={() => studentId && photoInputRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDraggingPhoto(true) }}
                onDragLeave={(e) => { e.preventDefault(); setIsDraggingPhoto(false) }}
                onDrop={handlePhotoDrop}
              >
                <div className={styles.photoIcon}>&#128247;</div>
                <div className={styles.photoDropzoneLabel}>фото</div>
                <div className={styles.photoDropzoneHint}>Нажмите или перетащите файл</div>
              </div>
              <button
                type='button'
                className={styles.photoUploadButton}
                disabled={!studentId}
                onClick={() => photoInputRef.current.click()}
              >
                Загрузить фото
              </button>
              <div className={styles.photoSidebarCaption}>JPG, PNG · до 5 МБ</div>
              </>
              )}
            </div>
            <ul>{variableStuffModalModes.map(renderLi)}</ul>
          </div>
          <div className={styles.modalMain}>{renderTab()}</div>
          <div className={styles.modalFooter}>
            <Button mode='bordered' onClick={() => setPasswordModal({ open: true, type: 'student' })}>Пароль</Button>
            <Button mode='bordered' onClick={() => setPasswordModal({ open: true, type: 'parent' })}>Пароль родителей</Button>
            <div className={styles.modalFooterRightBlock}>
              <div>
                {/* <div>Создал: Алехина Милана Дмитриевна 21.05.2022 15:34</div>
                <div>Изменил: Алехина Милана Дмитриевна 21.05.2022 15:34</div> */}
              </div>
              <Button mode='white' onClick={handleSaveClick}>Сохранить</Button>
              <Button mode='white' onClick={closeModal}>Закрыть</Button>
            </div>
          </div>
        </div>
        {confirmClose && (
          <Confirmation
            title="Закрыть без сохранения изменений?"
            acceptConfirmation={acceptConfirmation}
            rejectConfirmation={rejectConfirmation}
          />
        )}
      </Modal>
      <PasswordModal
        isOpen={passwordModal.open}
        type={passwordModal.type}
        studentId={studentId}
        onClose={() => setPasswordModal({ open: false, type: null })}
      />
    </StudentsContext.Provider>
  )
}
